import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { type SportTypes, isEventSportType } from '@racemap/utilities/consts/eventTypes';
import { MILE_TO_KM, NAUTICAL_MILE_TO_KM } from '@racemap/utilities/consts/units';
import { labelFormatDistance } from '@racemap/utilities/formattings/units';
import {
  distanceUnitByUnitType,
  unitAwareDistance,
  velocityUnitByUnitType,
} from '@racemap/utilities/functions/units';
import { unitAwareVelocity } from '@racemap/utilities/functions/units';
import {
  formatDurationAsHHHMMSS,
  getDefaultSpeed,
  getEventTypeById,
  isNotEmptyString,
} from '@racemap/utilities/functions/utils';
import { isDefined, isSplitPoint } from '@racemap/utilities/functions/validation';
import { roundTo } from '@racemap/utilities/roundTo';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import type { SplitObject } from '@racemap/utilities/types/geos';
import { UnitType } from '@racemap/utilities/types/units';
import { Button, Tooltip } from 'antd';
import type { Immutable } from 'immer';
import { Duration } from 'luxon';
import { type FC, useMemo, useState } from 'react';
import { useStore } from '../../../store/reducers';
import { IconArrowRight, IconSwitch } from '../../BasicComponents/Icons';
import { HStack, VStack } from '../../BasicComponents/MetaComponent';
import { type Column, type Entry, SimpleTable } from '../../BasicComponents/SimpleTable';
import { BlurFormInput } from '../../FormComponents';
import { presentDialogModal } from '../../Modal/presentDialogModal';
import SportChooser from '../../SportChooser';

interface Props {
  splits: Array<SplitObject>;
  unitType: UnitType;
  event: Immutable<RacemapEvent>;
}

interface SplitEntry extends Entry {
  id: string;
  index: number;
  segmentStart: number;
  segmentFinish: number | null;
  unitAwareLength: number;
  lengthInMeter: number;
  resetSpeed: number | null;
  minSpeed: number;
  maxSpeed: number;
  segmentStartName: string;
  segmentFinishName: string;
  sport: SportTypes | '' | null;
  split: SplitObject;
  isControlledSpeedSegment: boolean;
}

export const SpeedTable: FC<Props> = ({ splits, unitType, event }) => {
  const { updateGeoElement } = useStore((s) => s.events.actions);
  const [inputIsSpeed, setInputIsSpeed] = useState<boolean>(false);

  const data: Array<SplitEntry> = useMemo(
    () =>
      splits
        .sort(
          (a, b) =>
            (a.properties.offset ?? Number.POSITIVE_INFINITY) -
            (b.properties.offset ?? Number.NEGATIVE_INFINITY),
        )
        .map((split, index, splits) => {
          if (index === splits.length - 1) {
            return undefined;
          }
          const segmentStartInMeter = unitAwareDistance(split.properties.offset, unitType);
          const segmentFinishInMeter = unitAwareDistance(
            splits[index + 1]?.properties.offset,
            unitType,
          );
          const lengthInMeter =
            (splits[index + 1]?.properties.offset || 0) - (split.properties.offset || 0);
          const lengthInMeterOrFoot = segmentFinishInMeter - segmentStartInMeter;
          const resetSpeed = split.properties.resetSpeed;
          const sport = split.properties.newSport;

          const minSpeed = split.properties.minSpeed;
          const maxSpeed = split.properties.maxSpeed;
          const isControlledSpeedSegment = getIsControlledSpeedSegment(split);

          return {
            id: split.id,
            index: index + 1,
            segmentStart: segmentStartInMeter,
            segmentFinish: segmentFinishInMeter,
            unitAwareLength: lengthInMeterOrFoot,
            lengthInMeter,
            segmentFinishName: splits[index + 1]?.properties.name || '',
            segmentStartName: split.properties.name || '',
            resetSpeed,
            minSpeed,
            maxSpeed,
            sport,
            split,
            isControlledSpeedSegment,
          };
        })
        .filter(isDefined),
    [splits, unitType],
  );

  if (splits.length === 0) return <></>;

  const columns = [
    {
      header: 'Segment',
      key: 'index',
      dataKey: 'index',
      headerClassName: 'center',
      cellClassName: 'center',
      width: '75px',
      cellRenderer: (index) => <div>{index}</div>,
    } as Column<SplitEntry, 'index'>,
    {
      header: `From Split [${distanceUnitByUnitType(unitType)}]`,
      key: 'segmentStart',
      headerTitle: 'First point of the segment on the shadowtrack.',
      dataKey: 'segmentStart',
      cellRenderer: (segmentStartInMeter, splitEntry) => (
        <OffsetCell
          offset={segmentStartInMeter}
          splitName={splitEntry.segmentStartName}
          unitType={unitType}
        />
      ),
    } as Column<SplitEntry, 'segmentStart'>,
    {
      header: `To Split [${distanceUnitByUnitType(unitType)}]`,
      key: 'segmentFinish',
      headerTitle: 'Last point of the segment on the shadowtrack.',
      dataKey: 'segmentFinish',
      cellRenderer: (segmentFinishInMeter, splitEntry) => (
        <OffsetCell
          offset={segmentFinishInMeter}
          splitName={splitEntry.segmentFinishName}
          unitType={unitType}
        />
      ),
    } as Column<SplitEntry, 'segmentFinish'>,
    {
      header: `Length [${distanceUnitByUnitType(unitType)}]`,
      key: 'unitAwareLength',
      headerTitle: 'Length of the segment',
      dataKey: 'unitAwareLength',
      headerClassName: 'center',
      width: '100px',
      cellRenderer: (length) => (
        <LengthCell>{labelFormatDistance(length, unitType, 2, false)}</LengthCell>
      ),
    } as Column<SplitEntry, 'unitAwareLength'>,
    {
      header: 'Sport',
      key: 'sport',
      headerTitle:
        'Select the type of sport for the segment. You might adjust the value in the speed column also.',
      dataKey: 'sport',
      width: '250px',
      cellRenderer: (sport, splitEntry) =>
        sport == null ? (
          '-'
        ) : (
          <VStack style={{ width: '100%' }}>
            Select:
            <HStack width="100%" align="flex-end">
              <SportChooser
                value={sport || null}
                withSymbol
                onChange={async (newSport) => {
                  if (!isSplitPoint(splitEntry.split)) return;
                  let overwriteMinMax = false;
                  try {
                    await presentDialogModal({
                      title: 'Overwrite the min and max speed?',
                      dialog:
                        'You change the sport of this segment. Do you want to overwrite the min and max speed with the recommended values for the selected sport? We highly recommend, to set realistic values for that segment. Otherwise the prediction might be wrong.',
                      aktionButtonLabel: 'Overwrite',
                      cancelButtonLabel: 'Keep',
                      size: 'lg',
                    });
                    overwriteMinMax = true;
                  } catch {
                    // Keep the current values when the user declines.
                  }

                  const sport = getEventTypeById(newSport);
                  const minMaxSpeed =
                    overwriteMinMax && sport != null
                      ? {
                          minSpeed: sport.minSpeed,
                          maxSpeed: sport.maxSpeed,
                        }
                      : {};

                  updateGeoElement({
                    ...splitEntry.split,
                    properties: {
                      ...splitEntry.split.properties,
                      newSport: newSport != null ? (newSport as SportTypes) : '',
                      ...minMaxSpeed,
                    },
                  });
                }}
              />

              {isNotEmptyString(sport) && (
                <Tooltip title="Overwrite the min and max speed with the recommended values for the selected sport.">
                  <Button
                    style={{ marginLeft: 5 }}
                    onClick={() => {
                      if (!isSplitPoint(splitEntry.split)) return;
                      const newSpeeds = getSpeedsForSport(sport);

                      updateGeoElement({
                        ...splitEntry.split,
                        properties: {
                          ...splitEntry.split.properties,
                          ...newSpeeds,
                        },
                      });
                    }}
                    icon={<IconArrowRight />}
                  />
                </Tooltip>
              )}
            </HStack>
          </VStack>
        ),
    } as Column<SplitEntry, 'sport'>,
    {
      key: 'resetSpeed',
      dataKey: 'resetSpeed',
      minWidth: '400px',
      header: (
        <HStack justify="space-between" width="100%">
          <div>{`${
            inputIsSpeed ? `Speed [${velocityUnitByUnitType(unitType)}]` : 'Duration [hhh:mm:ss]'
          }`}</div>
          <div>
            <IconSwitch on={inputIsSpeed} onClick={() => setInputIsSpeed(!inputIsSpeed)} />
          </div>
        </HStack>
      ),
      cellRenderer: (_speed, splitEntry) => (
        <SpeedDurationCell
          unitType={unitType}
          splitEntry={splitEntry}
          event={event}
          inputIsSpeed={inputIsSpeed}
          onChange={(newSpeeds) =>
            isSplitPoint(splitEntry.split) &&
            updateGeoElement({
              ...splitEntry.split,
              properties: {
                ...splitEntry.split.properties,
                ...newSpeeds,
              },
            })
          }
        />
      ),
    } as Column<SplitEntry, 'resetSpeed'>,
  ];

  return (
    <Container>
      <SimpleTable<SplitEntry>
        data={data}
        expandable
        defaultExpanded={false}
        columns={columns as Array<Column<SplitEntry>>}
      />
    </Container>
  );
};

const OffsetCell: FC<{
  offset: number | null;
  unitType: UnitType;
  splitName: string;
}> = ({ offset, unitType, splitName }) => (
  <OffsetCellContainer>
    <div className="split-name">{splitName}</div>
    <div className="offset-container">
      <div className="label">Offset:</div> {labelFormatDistance(offset, unitType, 2, false)}
    </div>
  </OffsetCellContainer>
);

const LengthCell = styled.div` 
  align-self: flex-end;
  margin-bottom: 5px;
`;

const OffsetCellContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;

  .split-name {
    font-weight: bold;
    margin-bottom: 5px;
  }

  .offset-container {
    display: flex;
    justify-content: space-between;

    .label {
      font-weight: 400;
      color: ${RacemapColors.DarkGray};
    }
  }
`;

enum SpeedType {
  MIN = 'minSpeed',
  MAX = 'maxSpeed',
  RESET = 'resetSpeed',
}

const label: Record<SpeedType, string> = {
  [SpeedType.MIN]: 'Slow',
  [SpeedType.MAX]: 'Fast',
  [SpeedType.RESET]: 'Reset',
};

const SpeedDurationCell: FC<{
  splitEntry: SplitEntry;
  event: Immutable<RacemapEvent>;
  unitType: UnitType;
  inputIsSpeed: boolean;
  onChange: (newSpeeds: { resetSpeed: number | null; minSpeed: number; maxSpeed: number }) => void;
}> = ({ splitEntry, unitType, onChange, inputIsSpeed }) => {
  const handleSpeedChange = (newSpeed: number, type: SpeedType) => {
    if (onChange == null) return;

    const speeds = {
      minSpeed: splitEntry.minSpeed,
      maxSpeed: splitEntry.maxSpeed,
      resetSpeed: splitEntry.resetSpeed,
    };

    if (type === SpeedType.RESET && Number.isNaN(newSpeed)) {
      speeds.resetSpeed = null;
    } else {
      speeds[type] = newSpeed;
    }
    onChange(speeds);
  };

  return (
    <HStack justify="space-between" width="100%">
      {inputIsSpeed ? (
        <InputContainer>
          {Object.values(SpeedType).map((type) => (
            <VStack key={type}>
              <div>{label[type]}:</div>
              <SpeedInput
                value={splitEntry[type]}
                unitType={unitType}
                onChange={(newSpeed) => handleSpeedChange(newSpeed, type)}
              />
            </VStack>
          ))}
        </InputContainer>
      ) : (
        <InputContainer>
          {Object.values(SpeedType).map((type) => (
            <VStack key={type}>
              <div>{label[type]}:</div>
              <DurationInput
                speed={splitEntry[type]}
                unitType={unitType}
                onChange={(newSpeed) => handleSpeedChange(newSpeed, type)}
                length={splitEntry.lengthInMeter}
              />
            </VStack>
          ))}
        </InputContainer>
      )}
    </HStack>
  );
};

const InputContainer = styled(HStack)`
  width: 100%;

  div:not(:last-of-type) {
    margin-right: 5px;
  }
`;

const SpeedInput: FC<{
  value: number | null;
  onChange: (newSpeed: number) => void;
  unitType: UnitType;
}> = ({ value, onChange, unitType }) => {
  const valueString =
    value != null && value > 0 ? unitAwareVelocity(value, unitType).toFixed(2) : '';

  return (
    <BlurFormInput
      min={0.1}
      step={0.1}
      type="number"
      value={valueString}
      title={
        'Speed at which markers are moved from the start of this segment until the speed is corrected either by a new read or by a different segment speed.'
      }
      onChange={(newValue: string) => {
        if (unitType === UnitType.IMPERIAL) {
          onChange(roundTo(Number.parseFloat(newValue) * MILE_TO_KM, 2));
        } else if (unitType === UnitType.NAUTICAL) {
          onChange(roundTo(Number.parseFloat(newValue) * NAUTICAL_MILE_TO_KM, 2));
        } else {
          onChange(Number.parseFloat(newValue));
        }
      }}
      groupStyle={{ marginBottom: 'unset' }}
      style={{ textAlign: 'right' }}
    />
  );
};

const DurationInput: FC<{
  speed: number | null;
  onChange?: (newSpeed: number) => void;
  unitType: UnitType;
  length: number | null;
}> = ({ speed, onChange, length }) => {
  if (length == null) return <div>--</div>;
  const duration = getDuration({ speed, newSport: '', lengthInMeter: length });

  if (onChange == null) {
    return <div>{duration}</div>;
  }

  const handleChange = (newDuration: string) => {
    const newSpeed = getSpeed({ duration: newDuration, lengthInMeter: length });
    if (newSpeed == null) return;

    onChange(newSpeed);
  };

  return (
    <BlurFormInput
      type="text"
      value={duration}
      title={
        'Speed at which markers are moved from the start of this segment until the speed is corrected either by a new read or by a different segment speed.'
      }
      onChange={handleChange}
      groupStyle={{ marginBottom: 'unset', width: '100%' }}
      style={{ textAlign: 'right' }}
    />
  );
};

const Container = styled.div`
  td:not(:last-of-type),
  th:not(:last-of-type) {
    border-right: 1px solid #e0e0e0;
  }
`;

const getDuration = ({
  speed,
  newSport,
  lengthInMeter,
}: {
  speed: number | null;
  newSport: SportTypes | '' | null;
  lengthInMeter: number;
}) => {
  const sportBasedSpeed = isEventSportType(newSport) ? getDefaultSpeed(newSport) : null;
  const speedFinal = typeof speed === 'number' && speed > 0 ? speed : sportBasedSpeed;
  const duration = speedFinal
    ? Duration.fromObject({ hours: lengthInMeter / 1000 / speedFinal })
    : null;

  return duration != null ? formatDurationAsHHHMMSS(duration) : '-';
};

const getSpeed = ({
  duration,
  lengthInMeter,
}: {
  duration: string;
  lengthInMeter: number;
}): null | number => {
  const durationMoment = parseDuration(duration);
  if (!durationMoment.isValid || durationMoment.as('seconds') <= 0) return null;

  const durationInHours = durationMoment.as('hours');
  const speed = lengthInMeter / 1000 / durationInHours;

  return speed;
};

const parseDuration = (duration: string): Duration => {
  const parts = duration.split(':');
  if (parts.length !== 3) return Duration.invalid('Invalid duration format');

  const hours = Number.parseInt(parts[0], 10);
  const minutes = Number.parseInt(parts[1], 10);
  const seconds = Number.parseFloat(parts[2]);

  if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds)) {
    return Duration.invalid('Invalid duration values');
  }

  return Duration.fromObject({ hours, minutes, seconds });
};

const getSpeedsForSport = (
  sport: SportTypes | '' | null,
): { minSpeed: number; maxSpeed: number } => {
  const eventType = getEventTypeById(sport);
  if (eventType == null) return { minSpeed: 0.1, maxSpeed: 360 };

  const minSpeed = eventType.minSpeed || 0.1;
  const maxSpeed = eventType.maxSpeed;

  return { minSpeed, maxSpeed };
};

const getIsControlledSpeedSegment = (split: SplitObject): boolean => {
  return split.properties.resetSpeed != null;
};
OffsetCell;
